// Pure filter/sort/URL-sync logic for the capability register (spec §9),
// deliberately separated from CapabilityRegister.tsx so it's testable
// without touching the DOM. Client-side, over the whole GET /api/capabilities
// list — see CLAUDE.md for why (the dataset is small; shareable filtered
// URLs, saved views, and instant multi-select filtering all want the whole
// dataset in the browser, not server-side filter params).
//
// The 2026-08-27 register→issues migration (see CLAUDE.md) dropped every
// filter/sort/search field keyed on the register's now-frozen volatile
// columns (statusCategory, evidence, v0_parity — none can indicate a live
// gap ever again) and replaced statusCategory with issueState ("open"/
// "closed"), the Live signal that actually supersedes it.
import type { Capability } from "./capabilityTypes";

export type SortField = "id" | "name" | "plane" | "issueState" | "phase" | "roleCriticality";
export type SortDir = "asc" | "desc";
// "any" = no filter; "yes"/"no" = capability does/doesn't have a dedicated
// UI surface (real data: ~120 distinct free-text surface values, many of
// which are "(none — ...)"-shaped markers for API-only/infrastructure
// capabilities — not suited to multi-select, so this is its own tri-state).
export type HasSurface = "any" | "yes" | "no";

export interface FilterState {
  q: string;
  plane: string[];
  issueState: string[];
  phase: string[];
  roleCriticality: string[];
  missingAdr: boolean;
  hasSurface: HasSurface;
  sort: SortField;
  dir: SortDir;
  page: number;
}

export const DEFAULT_FILTER_STATE: FilterState = {
  q: "",
  plane: [],
  issueState: [],
  phase: [],
  roleCriticality: [],
  missingAdr: false,
  hasSurface: "any",
  sort: "id",
  dir: "asc",
  page: 1,
};

export const PAGE_SIZE = 50;

const SORT_FIELDS = new Set<SortField>(["id", "name", "plane", "issueState", "phase", "roleCriticality"]);

function parseList(v: string | null): string[] {
  if (!v) return [];
  return v
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
}

export function parseFilterState(search: string): FilterState {
  const params = new URLSearchParams(search);
  const page = Number.parseInt(params.get("page") ?? "1", 10);
  const sort = params.get("sort") ?? "id";
  const dir = params.get("dir") ?? "asc";
  const hasSurface = params.get("surface") ?? "any";
  return {
    q: params.get("q") ?? "",
    plane: parseList(params.get("plane")),
    issueState: parseList(params.get("state")),
    phase: parseList(params.get("phase")),
    roleCriticality: parseList(params.get("criticality")),
    missingAdr: params.get("missingAdr") === "1",
    hasSurface: hasSurface === "yes" || hasSurface === "no" ? hasSurface : "any",
    sort: SORT_FIELDS.has(sort as SortField) ? (sort as SortField) : "id",
    dir: dir === "desc" ? "desc" : "asc",
    page: Number.isFinite(page) && page >= 1 ? page : 1,
  };
}

// serializeFilterState omits any field left at its default, so the register
// table's default (unfiltered) view has a clean URL with no query string.
export function serializeFilterState(state: FilterState): string {
  const params = new URLSearchParams();
  if (state.q) params.set("q", state.q);
  if (state.plane.length) params.set("plane", state.plane.join(","));
  if (state.issueState.length) params.set("state", state.issueState.join(","));
  if (state.phase.length) params.set("phase", state.phase.join(","));
  if (state.roleCriticality.length) params.set("criticality", state.roleCriticality.join(","));
  if (state.missingAdr) params.set("missingAdr", "1");
  if (state.hasSurface !== "any") params.set("surface", state.hasSurface);
  if (state.sort !== "id") params.set("sort", state.sort);
  if (state.dir !== "asc") params.set("dir", state.dir);
  if (state.page !== 1) params.set("page", String(state.page));
  const s = params.toString();
  return s ? `?${s}` : "";
}

function hasNoUISurface(c: Capability): boolean {
  return c.snapshot.surface.trim().startsWith("(");
}

// matchesSearch only searches fields actually present in the wire format —
// the volatile tier (current_status, notes, evidence, ...) is omitted from
// the API response entirely (see capabilityTypes.ts), so it's not
// searchable here either. GET /api/search still full-text-scans the
// backend's own internal register.Capability domain type, which does keep
// those fields — this local register-table search is deliberately narrower,
// matching what the browser actually has.
function matchesSearch(c: Capability, q: string): boolean {
  const needle = q.toLowerCase();
  return [c.id, c.name, c.snapshot.surface, c.snapshot.futureSurface].join(" ␟ ").toLowerCase().includes(needle);
}

// fieldValue reads a SortField off a Capability, accounting for the one
// field (phase) that moved under snapshot in the 2026-08-27 migration —
// exported so CapabilityRegister.tsx's filter-option-distinct-values logic
// uses the identical accessor rather than re-deriving it.
export function fieldValue(c: Capability, field: SortField): string {
  if (field === "phase") return c.snapshot.phase;
  return c[field];
}

function compareBy(field: SortField, a: Capability, b: Capability): number {
  return (fieldValue(a, field) ?? "").localeCompare(fieldValue(b, field) ?? "");
}

// applyFilters filters and sorts (never paginates — see paginate) the full
// capability list. roles/adrReferences may be null even though the backend
// now always sends [] (defensive typing, see capabilityTypes.ts), so every
// array access here tolerates null.
export function applyFilters(caps: Capability[], state: FilterState): Capability[] {
  let rows = caps;

  if (state.q.trim()) {
    rows = rows.filter((c) => matchesSearch(c, state.q.trim()));
  }
  if (state.plane.length) {
    rows = rows.filter((c) => state.plane.includes(c.plane));
  }
  if (state.issueState.length) {
    rows = rows.filter((c) => state.issueState.includes(c.issueState));
  }
  if (state.phase.length) {
    rows = rows.filter((c) => state.phase.includes(c.snapshot.phase));
  }
  if (state.roleCriticality.length) {
    rows = rows.filter((c) => state.roleCriticality.includes(c.roleCriticality));
  }
  if (state.missingAdr) {
    rows = rows.filter((c) => (c.adrReferences ?? []).length === 0);
  }
  if (state.hasSurface !== "any") {
    const wantNoSurface = state.hasSurface === "no";
    rows = rows.filter((c) => hasNoUISurface(c) === wantNoSurface);
  }

  const sorted = [...rows].sort((a, b) => {
    const primary = compareBy(state.sort, a, b);
    const signed = state.dir === "desc" ? -primary : primary;
    return signed !== 0 ? signed : a.id.localeCompare(b.id);
  });
  return sorted;
}

export function paginate(rows: Capability[], page: number): Capability[] {
  const start = (page - 1) * PAGE_SIZE;
  return rows.slice(start, start + PAGE_SIZE);
}

export interface PresetView {
  id: string;
  label: string;
  description: string;
  state: Partial<FilterState>;
}

// PRESET_VIEWS implements spec §9's suggested register views, reduced from
// ten to eight by the 2026-08-27 register→issues migration: "V0 parity" and
// "Missing evidence" are dropped outright (no successor data — v0_parity and
// evidence are both volatile, frozen forever, never a live signal again),
// not kept and silently repointed at something else. Grounded against the
// real register (owner-confirmed by construction: every remaining preset
// below was checked against the live 425-row corpus before shipping — see
// CLAUDE.md). Two are necessarily approximations rather than a clean
// structured field:
//   - "Governance and security" maps to the TRUST/CHANGE planes (Cortex
//     Atlas's own architectural domains for trust/security and change
//     control — not an OBASHI B-A-S-H-I plane), the closest real grouping.
//   - "Front-end gaps" combines "still open" with hasSurface=yes (surface
//     is ~120 free-text values, not a clean category — "has a dedicated UI
//     surface at all" is the groundable signal, not a specific front-end
//     tag).
export const PRESET_VIEWS: PresetView[] = [
  {
    id: "executive-summary",
    label: "Executive summary",
    description: "The full register, sorted by issue state for a top-level read.",
    state: { sort: "issueState", dir: "asc" },
  },
  {
    id: "high-risk-gaps",
    label: "High-risk gaps",
    description: "Still-open, high-criticality capabilities.",
    state: { issueState: ["open"], roleCriticality: ["high"] },
  },
  {
    id: "front-end-gaps",
    label: "Front-end gaps",
    description: "Still-open capabilities that have a dedicated UI surface.",
    state: { issueState: ["open"], hasSurface: "yes" },
  },
  {
    id: "governance-and-security",
    label: "Governance and security",
    description: "Capabilities in the trust/security and change-control domains.",
    state: { plane: ["TRUST", "CHANGE"] },
  },
  {
    id: "no-human-surface",
    label: "No human surface",
    description: "API-only or infrastructure capabilities with no dedicated UI.",
    state: { hasSurface: "no" },
  },
  {
    id: "phase-1-readiness",
    label: "Phase 1 readiness",
    description: "Capabilities scoped to delivery phase 1 (frozen at the 2026-08-27 migration).",
    state: { phase: ["1"] },
  },
  {
    id: "shipped-with-narrowing",
    label: "Shipped (narrow further)",
    description: "Everything shipped (issue closed) — add search or filters to narrow.",
    state: { issueState: ["closed"] },
  },
  {
    id: "missing-adr",
    label: "Missing ADR",
    description: "Capabilities with no governing ADR reference.",
    state: { missingAdr: true },
  },
];
